import { useEffect, useState, useRef, useCallback } from "react";
import ProductCard from "features/products/components/ProductCard";
import SearchHistory from "features/search/components/SearchHistory";
import { useTranslation } from "context/LanguageContext";
import { FaSearch, FaHome } from "react-icons/fa";
import { MdClear } from "react-icons/md";
import { FaRegFaceSadTear } from "react-icons/fa6";
import { searchProductsAdvanced } from "api/repository/products.api";
import CartSidebar from "shared/components/cart/CartSidebar";
import { useNavigate } from "react-router-dom";
import { useCart } from "context/CartContext";
import CartButton from "shared/components/cart/CartButton";

const LIMIT = 20;

export default function SearchPage() {
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [error, setError] = useState(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [showBar, setShowBar] = useState(true);
  const [isCartOpen, setIsCartOpen] = useState(false);

  const { getCartCount } = useCart();
  const cartCount = getCartCount();
  const { t, language } = useTranslation();
  const isRTL = language === "ar";
  const navigate = useNavigate();

  const lastProductRef = useRef();
  const searchSavedRef = useRef(false);
  const scrollTimeout = useRef(null);

  // Debounce query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
      setOffset(0);
      setProducts([]);
      searchSavedRef.current = false;
    }, 1500);
    return () => clearTimeout(timer);
  }, [query]);

  // Hide/show bar on scroll
  useEffect(() => {
    const handleScroll = () => {
      setShowBar(false);
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
      scrollTimeout.current = setTimeout(() => setShowBar(true), 150);
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    };
  }, []);

  // Fetch
   useEffect(() => {
    const fetchSearchAPI = async () => {
      if (!debouncedQuery || debouncedQuery.trim().length < 2) {
        setProducts([]);
        setHasMore(false);
        return;
      }

      try {
        offset === 0 ? setLoading(true) : setLoadingMore(true);
        setError(null);

      const response = await searchProductsAdvanced(debouncedQuery, {
        limit: LIMIT,
        offset,
      });

        const newProducts = response?.data ?? [];

        setProducts(prev => offset === 0 ? newProducts : [...prev, ...newProducts]);
        setHasMore(newProducts.length === LIMIT);

        if (offset === 0 && newProducts.length > 0 && !searchSavedRef.current) {
          window.saveSearchHistory?.(debouncedQuery);
          searchSavedRef.current = true;
        }
      } catch (err) {
        console.error("Search error:", err);
        setError(t("searchError"));
        if (offset === 0) setProducts([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    };

    fetchSearchAPI();
  }, [debouncedQuery, offset, t]);

  // Infinite scroll observer
  const handleObserver = useCallback(
    (entries) => {
      if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
        setOffset(prev => prev + LIMIT);
      }
    },
    [hasMore, loading, loadingMore]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      rootMargin: "200px",
      threshold: 0,
    });
    if (lastProductRef.current) observer.observe(lastProductRef.current);
    return () => observer.disconnect();
  }, [handleObserver, products]);

  const handleClearSearch = () => {
    setQuery("");
    setProducts([]);
    setOffset(0);
    setHasMore(false);
  };

  return (
    <div className="container mx-auto px-4 py-8" dir={isRTL ? "rtl" : "ltr"}>
      {/* Fixed Bar */}
      <div className={`fixed top-0 left-0 right-0 bg-white z-50 shadow-md transition-transform duration-300 ${showBar ? "translate-y-0" : "-translate-y-full"}`}>
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-4" style={{ flexDirection: isRTL ? "row-reverse" : "row" }}>
          <button onClick={() => navigate("/")} aria-label="Go to home">
            <FaHome className="w-6 h-6 text-blue-600 mx-auto" />
          </button>
          <CartButton cartCount={cartCount} cartStatus={setIsCartOpen} setHide={true} />
          <div className="relative flex-1">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`${t("searchForOtherProducts")}...`}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:bg-slate-50"
              style={{
                textAlign: isRTL ? "right" : "left",
                paddingRight: isRTL ? "3rem" : "1rem",
                paddingLeft: isRTL ? "1rem" : "3rem",
              }}
              dir={isRTL ? "rtl" : "ltr"}
            />
            {query && (
              <button
                onClick={handleClearSearch}
                className="absolute top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                style={{ [isRTL ? "right" : "left"]: "1rem" }}
                aria-label="Clear search"
              >
                <MdClear />
              </button>
            )}
            <div
              className="absolute top-1/2 -translate-y-1/2 text-gray-400"
              style={{ [isRTL ? "left" : "right"]: "1rem" }}
            >
              <FaSearch />
            </div>
          </div>
        </div>
      </div>

      <div className="h-10 mb-6" />

      <SearchHistory onSelectSearch={(term) => setQuery(term)} currentQuery={query} />

      {/* States */}
      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
          <p className="mt-4 text-gray-600">{t("searching")}</p>
        </div>
      )}

      {error && !loading && (
        <div className="text-center py-12">
          <p className="text-red-500">{error}</p>
        </div>
      )}

      {!query && !loading && (
        <div className="text-center py-12">
          <FaSearch className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">{t("startTyping")}</p>
        </div>
      )}

      {query && !loading && !error && products.length === 0 && debouncedQuery && (
        <div className="text-center py-12">
          <FaRegFaceSadTear className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">{t("noResultsFound")} "{query}"</p>
        </div>
      )}

      {/* Products Grid */}
      {!loading && products.length > 0 && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {products.map((product, index) => (
              <div
                key={product.id}
                className="cursor-pointer"
                ref={index === products.length - 1 ? lastProductRef : null}
              >
                <ProductCard product={product} />
              </div>
            ))}
          </div>

          {loadingMore && (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
            </div>
          )}
        </>
      )}

      <CartSidebar isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </div>
  );
}
